/**
 * 대화 번들 export/import — 결정 #6. 한 롤플레잉 세션을 통째로(대화 + 메시지 +
 * 캐릭터 + 임베드 로어북) 단일 JSON으로 공유한다. storage.exportAll(백업)과 달리
 * "이 대화만" 골라 가져갈 수 있다.
 *
 * 핵심: **ID 재매핑**(결정 #9/#20). 가져올 때 모든 엔티티에 새 id를 부여하고
 * 교차 참조(chat↔message↔character↔lorebook, participant)를 일관되게 다시 잇는다.
 * 그래서 기존 데이터와 충돌하지 않고, 번들에 없는 참조(페르소나/프리셋/스크립트/
 * 아바타)는 깔끔히 비워 고아 참조를 만들지 않는다.
 *
 * 플랫폼 중립(결정 #10): 저장소/blob에 접근하지 않는다. 호출부(web)가 엔티티를
 * 모아 넘기고, 결과를 저장한다.
 */
import type {
  Character,
  CharacterId,
  Chat,
  ChatId,
  Lorebook,
  LorebookId,
  Message,
  ParticipantId,
} from "../domain/index.js";
import {
  newCharacterId,
  newChatId,
  newLorebookId,
  newMessageId,
  newParticipantId,
} from "../util/id.js";
import { newSyncMeta } from "../util/sync-meta.js";

/** 직렬화 가능한 대화 번들(결정 #6). */
export interface ConversationBundle {
  spec: "roqeet-conversation_v1";
  exportedAt: number;
  chat: Chat;
  messages: Message[];
  characters: Character[];
  lorebooks: Lorebook[];
}

/** 가져오기 결과(모두 새 id, 참조 재매핑 완료). 호출부가 저장한다. */
export interface ImportedConversation {
  chat: Chat;
  messages: Message[];
  characters: Character[];
  lorebooks: Lorebook[];
}

/**
 * 대화 + 그에 딸린 엔티티로 번들을 만든다(결정 #6). 호출부가 chat의 참가자/로어북
 * 참조를 따라 character/lorebook을 모아 넘긴다.
 */
export function buildConversationBundle(
  chat: Chat,
  messages: Message[],
  characters: Character[],
  lorebooks: Lorebook[],
): ConversationBundle {
  return {
    spec: "roqeet-conversation_v1",
    exportedAt: Date.now(),
    chat,
    messages: messages.filter((m) => !m.sync.deletedAt).sort((a, b) => a.order - b.order),
    characters,
    lorebooks,
  };
}

/** 번들을 JSON 문자열로 직렬화. */
export function exportConversationToJson(bundle: ConversationBundle): string {
  return JSON.stringify(bundle, null, 2);
}

/**
 * JSON 텍스트에서 대화 번들을 가져와 모든 id를 재매핑한다(결정 #6/#9). 충돌 없는
 * 새 엔티티 집합을 반환하며, 번들에 없는 참조(페르소나/프리셋/스크립트/아바타)는
 * 비워 고아 참조를 만들지 않는다.
 */
export function importConversationFromJson(text: string): ImportedConversation {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("대화 번들 JSON을 해석할 수 없습니다.");
  }
  const b = parsed as Partial<ConversationBundle>;
  if (b.spec !== "roqeet-conversation_v1" || !b.chat) {
    throw new Error("유효한 대화 번들이 아닙니다(spec 불일치).");
  }
  return remapConversation({
    chat: b.chat,
    messages: b.messages ?? [],
    characters: b.characters ?? [],
    lorebooks: b.lorebooks ?? [],
  });
}

/** 번들 엔티티 전체에 새 id를 부여하고 교차 참조를 다시 잇는다(결정 #9/#20). */
function remapConversation(src: {
  chat: Chat;
  messages: Message[];
  characters: Character[];
  lorebooks: Lorebook[];
}): ImportedConversation {
  // 1) id 매핑 테이블 구성.
  const lorebookMap = new Map<string, LorebookId>();
  for (const lb of src.lorebooks) lorebookMap.set(lb.id, newLorebookId());

  const charMap = new Map<string, CharacterId>();
  for (const c of src.characters) charMap.set(c.id, newCharacterId());

  const newChatIdValue: ChatId = newChatId();
  const participantMap = new Map<string, ParticipantId>();
  for (const p of src.chat.participants) participantMap.set(p.id, newParticipantId());

  // 2) 로어북 재매핑(새 id + 새 sync). 엔트리는 nested라 그대로 둔다.
  const lorebooks: Lorebook[] = src.lorebooks.map((lb) => ({
    ...lb,
    id: lorebookMap.get(lb.id)!,
    sync: newSyncMeta(),
  }));

  // 3) 캐릭터 재매핑. lorebookId는 맵으로, 번들에 없는 참조(scripts/avatar)는 제거.
  const characters: Character[] = src.characters.map((c) => {
    const next: Character = {
      ...c,
      id: charMap.get(c.id)!,
      sync: newSyncMeta(),
      scriptIds: [], // 스크립트는 번들에 없음 → 고아 참조 방지
    };
    delete next.avatarAssetId; // 에셋 바이트가 없으므로 참조 제거
    if (c.lorebookId && lorebookMap.has(c.lorebookId)) {
      next.lorebookId = lorebookMap.get(c.lorebookId)!;
    } else {
      delete next.lorebookId;
    }
    return next;
  });

  // 4) 대화 재매핑. 참가자 id/characterId, lorebookIds 재연결. persona/preset 제거.
  const chat: Chat = {
    ...src.chat,
    id: newChatIdValue,
    sync: newSyncMeta(),
    participants: src.chat.participants.map((p) => {
      const np = { ...p, id: participantMap.get(p.id)! };
      if (p.characterId && charMap.has(p.characterId)) {
        np.characterId = charMap.get(p.characterId)!;
      } else {
        delete np.characterId;
      }
      // 페르소나는 번들에 없음 → 비워서 런타임 기본 페르소나로 폴백.
      delete np.personaId;
      return np;
    }),
    lorebookIds: src.chat.lorebookIds
      .filter((id) => lorebookMap.has(id))
      .map((id) => lorebookMap.get(id)!),
  };
  delete chat.presetId; // 프리셋은 번들에 없음 → 기본 프리셋 사용

  // 5) 메시지 재매핑. 새 id + 새 chatId + characterId/participant 재연결.
  const messages: Message[] = src.messages
    .filter((m) => !m.sync.deletedAt)
    .map((m) => {
      const next: Message = {
        ...m,
        id: newMessageId(),
        chatId: newChatIdValue,
        sync: newSyncMeta(),
        variants: m.variants.map((v) => ({
          ...v,
          ...(v.editHistory ? { editHistory: [...v.editHistory] } : {}),
          // 인라인 에셋/생성이미지 참조는 번들에 없음 → 제거(고아 방지).
          assetIds: undefined,
          generatedImageIds: undefined,
        })),
      };
      if (m.characterId && charMap.has(m.characterId)) {
        next.characterId = charMap.get(m.characterId)!;
      } else {
        delete next.characterId;
      }
      if (m.senderParticipantId && participantMap.has(m.senderParticipantId)) {
        next.senderParticipantId = participantMap.get(m.senderParticipantId)!;
      } else {
        delete next.senderParticipantId;
      }
      delete next.personaId;
      return next;
    });

  chat.messageCount = messages.length;
  return { chat, messages, characters, lorebooks };
}
